import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import type { Rect } from "@/lib/math/rect";
import { sampleEdgeBezier as sampleEdgeBezierKernel } from "./curveKernels";

export interface Point {
  x: number;
  y: number;
}

export interface SampledVectorRegion {
  edgeIds: string[];
  edgeRefs: Array<{ edgeId: string; forward: boolean }>;
  points: Point[];
}

export type CurveStepSource = number | ((edge: VectorEdge) => number);

type RegionContourBackend = (
  network: VectorNetwork,
  steps: CurveStepSource,
) => SampledVectorRegion[];

let regionContourBackend: RegionContourBackend = sampleNetworkRegionContoursTs;

/** Register Wasm (or test) backend for closed-network region sampling. */
export function setRegionContourBackend(backend: RegionContourBackend): void {
  regionContourBackend = backend;
}

export function resetRegionContourBackendForTests(): void {
  regionContourBackend = sampleNetworkRegionContoursTs;
}

export function sampleNetworkContour(network: VectorNetwork, steps: CurveStepSource = 24): Point[] {
  return sampleEdges(network, network.edges, steps);
}

export function sampleNetworkContours(network: VectorNetwork, steps: CurveStepSource = 24): Point[][] {
  return sampleNetworkRegionContours(network, steps).map(region => region.points);
}

export function isClosedVectorNetwork(network: VectorNetwork): boolean {
  return sampleNetworkRegionContours(network).length > 0;
}

export function sampleNetworkRegionContours(network: VectorNetwork, steps: CurveStepSource = 24): SampledVectorRegion[] {
  return regionContourBackend(network, steps);
}

function sampleNetworkRegionContoursTs(network: VectorNetwork, steps: CurveStepSource = 24): SampledVectorRegion[] {
  const verticesById = vertexLookup(network.vertices);
  const halfEdges = network.edges.flatMap(edge => [
    halfEdgeFor(edge, true, verticesById),
    halfEdgeFor(edge, false, verticesById),
  ]).filter((halfEdge): halfEdge is HalfEdge => Boolean(halfEdge));
  const outgoing = new Map<string, HalfEdge[]>();
  for (const halfEdge of halfEdges) {
    outgoing.set(halfEdge.fromVertexId, [...(outgoing.get(halfEdge.fromVertexId) ?? []), halfEdge]);
  }
  for (const edges of outgoing.values()) {
    edges.sort((left, right) => left.angle - right.angle);
  }

  const visited = new Set<string>();
  const regions: SampledVectorRegion[] = [];
  for (const start of halfEdges) {
    if (visited.has(start.key)) {
      continue;
    }

    const face: HalfEdge[] = [];
    let current: HalfEdge | undefined = start;
    while (current && !visited.has(current.key)) {
      visited.add(current.key);
      face.push(current);
      const candidates: HalfEdge[] = outgoing.get(current.toVertexId) ?? [];
      const reverseKey = current.reverseKey;
      const reverseIndex = candidates.findIndex((candidate: HalfEdge) => candidate.key === reverseKey);
      if (reverseIndex < 0 || candidates.length === 0) {
        current = undefined;
        break;
      }
      current = candidates[(reverseIndex - 1 + candidates.length) % candidates.length];
    }

    if (current?.key !== start.key || face.length < 3) {
      continue;
    }

    const points = sampleHalfEdges(verticesById, face, steps);
    if (points.length < 3 || signedArea(points) <= 0.001) {
      continue;
    }

    regions.push({
      edgeIds: face.map(halfEdge => halfEdge.edge.id),
      edgeRefs: face.map(halfEdge => ({ edgeId: halfEdge.edge.id, forward: halfEdge.forward })),
      points,
    });
  }

  return regions;
}

interface HalfEdge {
  key: string;
  reverseKey: string;
  edge: VectorEdge;
  forward: boolean;
  fromVertexId: string;
  toVertexId: string;
  angle: number;
}

function halfEdgeFor(edge: VectorEdge, forward: boolean, vertices: Map<string, VectorVertex>): HalfEdge | undefined {
  const from = vertices.get(forward ? edge.from.vertexId : edge.to.vertexId);
  const to = vertices.get(forward ? edge.to.vertexId : edge.from.vertexId);
  if (!from || !to) {
    return undefined;
  }

  const tangentTarget = forward
    ? edge.from.handle ?? to
    : edge.to.handle ?? to;
  const dx = tangentTarget.x - from.x;
  const dy = tangentTarget.y - from.y;
  return {
    key: halfEdgeKey(edge.id, forward),
    reverseKey: halfEdgeKey(edge.id, !forward),
    edge,
    forward,
    fromVertexId: from.id,
    toVertexId: to.id,
    angle: Math.atan2(dy, dx),
  };
}

function halfEdgeKey(edgeId: string, forward: boolean): string {
  return `${edgeId}:${forward ? "f" : "r"}`;
}

function sampleHalfEdges(verticesById: Map<string, VectorVertex>, halfEdges: HalfEdge[], steps: CurveStepSource): Point[] {
  return halfEdges.flatMap((halfEdge, index) => {
    const sampled = sampleEdgePoints(verticesById, halfEdge.edge, stepsForEdge(steps, halfEdge.edge), halfEdge.forward);
    return index === 0 ? sampled : sampled.slice(1);
  });
}

function vertexLookup(vertices: VectorVertex[]): Map<string, VectorVertex> {
  const map = new Map<string, VectorVertex>();
  for (const vertex of vertices) {
    map.set(vertex.id, vertex);
  }
  return map;
}

function signedArea(points: Point[]): number {
  return points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return area + point.x * next.y - next.x * point.y;
  }, 0) / 2;
}

function sampleEdges(network: VectorNetwork, edges: VectorEdge[], steps: CurveStepSource): Point[] {
  const verticesById = vertexLookup(network.vertices);
  return edges.flatMap(edge => {
    return sampleEdgePoints(verticesById, edge, stepsForEdge(steps, edge), true).slice(0, -1);
  });
}

function stepsForEdge(source: CurveStepSource, edge: VectorEdge): number {
  const steps = typeof source === "number" ? source : source(edge);
  return Math.max(1, Math.floor(steps));
}

function sampleEdgePoints(vertices: Map<string, VectorVertex>, edge: VectorEdge, steps: number, forward: boolean): Point[] {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  const points = sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, steps);
  return forward ? points : [...points].reverse();
}

export function edgeHasCurve(fromHandle?: Point, toHandle?: Point): boolean {
  return Boolean(fromHandle || toHandle);
}

export function edgeBezierControls(
  from: Point,
  to: Point,
  fromHandle?: Point,
  toHandle?: Point,
): { p0: Point; p1: Point; p2: Point; p3: Point } | undefined {
  if (!edgeHasCurve(fromHandle, toHandle)) {
    return undefined;
  }

  return {
    p0: from,
    p1: fromHandle ?? from,
    p2: toHandle ?? to,
    p3: to,
  };
}

export function sampleEdgeBezier(
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
  steps: number,
): Point[] {
  // Shared P4 seam — Wasm when enabled, otherwise TS (see curveKernels).
  return sampleEdgeBezierKernel(from, to, fromHandle, toHandle, steps);
}

export function edgeBezierBoundsPoints(
  from: Point,
  to: Point,
  fromHandle?: Point,
  toHandle?: Point,
): Point[] {
  const controls = edgeBezierControls(from, to, fromHandle, toHandle);
  if (!controls) {
    return [from, to];
  }

  return cubicBoundsPoints(controls.p0, controls.p1, controls.p2, controls.p3);
}

export function getVectorNetworkBounds(network: VectorNetwork): Rect {
  const verticesById = vertexLookup(network.vertices);
  const points = [
    ...network.vertices,
    ...network.edges.flatMap(edge => getEdgeBoundsPoints(verticesById, edge)),
  ];

  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function getEdgeBoundsPoints(vertices: Map<string, VectorVertex>, edge: VectorEdge): Point[] {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  return edgeBezierBoundsPoints(from, to, edge.from.handle, edge.to.handle);
}

function cubicBoundsPoints(p0: Point, p1: Point, p2: Point, p3: Point): Point[] {
  const tValues = new Set([0, 1]);
  for (const t of cubicExtrema(p0.x, p1.x, p2.x, p3.x)) {
    tValues.add(t);
  }
  for (const t of cubicExtrema(p0.y, p1.y, p2.y, p3.y)) {
    tValues.add(t);
  }

  return Array.from(tValues).map(t => evaluateCubic(p0, p1, p2, p3, t));
}

function cubicExtrema(p0: number, p1: number, p2: number, p3: number): number[] {
  const a = -p0 + 3 * p1 - 3 * p2 + p3;
  const b = 3 * p0 - 6 * p1 + 3 * p2;
  const c = -3 * p0 + 3 * p1;
  const qa = 3 * a;
  const qb = 2 * b;
  const qc = c;

  if (Math.abs(qa) < 0.0000001) {
    if (Math.abs(qb) < 0.0000001) {
      return [];
    }

    const t = -qc / qb;
    return isUnitInterval(t) ? [t] : [];
  }

  const discriminant = qb * qb - 4 * qa * qc;
  if (discriminant < 0) {
    return [];
  }

  const root = Math.sqrt(discriminant);
  return [(-qb + root) / (2 * qa), (-qb - root) / (2 * qa)].filter(isUnitInterval);
}

function isUnitInterval(value: number): boolean {
  return value > 0 && value < 1;
}

function evaluateCubic(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
    y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y,
  };
}
